import React, { Fragment } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import { preventNegativeValueInDocs } from "_helpers/helpers";
import { SignPad } from "Docs/Components";

/**
 * Idaho Form ID W-4 — page 1: instructions + certificate + signature;
 * page 2: worksheets / instructions (continued).
 */
export default function StateTaxBoiseView({
  form,
  errors,
  sign,
  page,
  onFormChange,
  onSsnChange,
  onSignature,
  onDateChange,
  setSignContainer,
}) {
  if (page === 1) {
    return (
      <div className="row state-tax-boise-w4">
        <div className="col-md-12">
          <h3 className="mt-0">General Instructions</h3>
          <p>
            Complete Form ID W-4 so your employer can withhold the correct amount
            of state income tax from your paycheck. Sign the form and give it to
            your employer. <strong>Use the information on the next page</strong>{" "}
            if you need help calculating your Idaho allowances or any additional
            amount to withhold from each paycheck. If you plan to itemize
            deductions, use the worksheet at{" "}
            <a
              href="https://tax.idaho.gov/w4"
              target="_blank"
              rel="noopener noreferrer"
            >
              tax.idaho.gov/w4
            </a>
            .
          </p>

          <h4>Purpose of Form</h4>
          <p>
            The withholding status you choose (A, B, or C) tells your employer
            how to apply Idaho withholding tables. Read each option carefully,
            then complete the certificate below the cut line.
          </p>

          <h4>Withholding status</h4>
          <p>
            Check the &quot;<strong>A</strong>&quot; box (Single) if you&apos;re:
          </p>
          <ul className="state-tax-boise-list">
            <li>Single with one job or single with multiple jobs</li>
            <li>Filing as head of household</li>
          </ul>

          <p>
            Check the &quot;<strong>B</strong>&quot; box (Married) if you&apos;re:
          </p>
          <ul className="state-tax-boise-list">
            <li>
              Married filing jointly with one job and your spouse doesn&apos;t
              work
            </li>
            <li>
              A qualifying surviving spouse with qualifying dependents
            </li>
          </ul>

          <p>
            Check the &quot;<strong>C</strong>&quot; box (Married, but withhold
            at Single rate) if you&apos;re:
          </p>
          <ul className="state-tax-boise-list">
            <li>
              Married filing jointly and both people work (or you have multiple
              jobs)
            </li>
            <li>Married filing separately</li>
          </ul>

          <p className="disclaimers">
            <strong>Tip:</strong> Enter your withholding status, allowances, and
            signature on the certificate below. Optional Line 1 and Line 2
            worksheets and tables are on the next page.
          </p>

          <div className="state-tax-section">
            <div className="state-tax-boise-withholding-prompt">
              <strong>WITHHOLDING STATUS</strong> (see information above)
            </div>

            <div className="form-group state-tax-select-one">
              <span>Select one:</span>
              <div className="state-tax-checkbox-group">
                <span className="state-tax-checkbox-label">
                  <input
                    type="checkbox"
                    field="form.filing_status_single"
                    checked={!!form.filing_status_single}
                    onChange={onFormChange}
                  />
                  Single
                </span>
                <span className="state-tax-checkbox-label">
                  <input
                    type="checkbox"
                    field="form.filing_status_married"
                    checked={!!form.filing_status_married}
                    onChange={onFormChange}
                  />
                  Married
                </span>
                <span className="state-tax-checkbox-label">
                  <input
                    type="checkbox"
                    field="form.filing_status_married_single_rate"
                    checked={!!form.filing_status_married_single_rate}
                    onChange={onFormChange}
                  />
                  Married, but withhold at the higher single rate.
                </span>
              </div>
            </div>

            <div className="state-tax-boise-stack-field">
              <div className="state-tax-boise-stack-field-label">
                <ul>
                  <li>
                     Total number of Idaho allowances you&apos;re
                    claiming
                  </li>
                </ul>
              </div>
              <div className="state-tax-boise-stack-field-input">
                <input
                  type="number"
                  placeholder="- Enter here -"
                  className={`${
                    errors["form.allowances"] ? "error" : ""
                  } price_input`}
                  name="allowances"
                  id="boise_allowances"
                  value={form.allowances ?? ""}
                  field="form.allowances"
                  onChange={onFormChange}
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={preventNegativeValueInDocs}
                />
                {errors["form.allowances"] && (
                  <label htmlFor="boise_allowances" className="error_label">
                    {errors["form.allowances"]}
                  </label>
                )}
              </div>
            </div>

            <div className="state-tax-boise-stack-field">
              <div className="state-tax-boise-stack-field-label">
                <ul>
                  <li>
                     Additional amount (if any) you need
                    withheld from each paycheck (enter whole dollars)
                  </li>
                </ul>
              </div>
              <div className="state-tax-boise-stack-field-input">
                <input
                  type="number"
                  placeholder="- Enter here -"
                  className={`${
                    errors["form.additional_amount"] ? "error" : ""
                  } price_input`}
                  name="additional_amount"
                  id="boise_additional_amount"
                  value={form.additional_amount ?? ""}
                  field="form.additional_amount"
                  onChange={onFormChange}
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={preventNegativeValueInDocs}
                />
                {errors["form.additional_amount"] && (
                  <label
                    htmlFor="boise_additional_amount"
                    className="error_label"
                  >
                    {errors["form.additional_amount"]}
                  </label>
                )}
              </div>
            </div>

            <div className="form-group">
                <span>Your Social Security number (required)</span>
                <input
                  type="text"
                  className="form-control"
                  value={form.ssn || ""}
                  onChange={onSsnChange}
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                />
              </div>

              <div className="form-row state-tax-name-row">
                <div className="form-group state-tax-first-name flex-1">
                  <span>First name and middle initial</span>
                  <input
                    type="text"
                    className="form-control"
                    field="form.first_name"
                    value={form.first_name || ""}
                    onChange={onFormChange}
                    placeholder="First name"
                  />
                </div>
                <div className="form-group state-tax-initial flex-1">
                  <span>M.I.</span>
                  <input
                    type="text"
                    className="form-control"
                    field="form.middle_initial"
                    value={form.middle_initial || ""}
                    onChange={onFormChange}
                    placeholder=""
                    maxLength={2}
                  />
                </div>
                <div className="form-group state-tax-last-name flex-1">
                  <span>Last name</span>
                  <input
                    type="text"
                    className="form-control"
                    field="form.last_name"
                    value={form.last_name || ""}
                    onChange={onFormChange}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <span>Current mailing address</span>
                <input
                  type="text"
                  className="form-control"
                  field="form.address"
                  value={form.address || ""}
                  onChange={onFormChange}
                  placeholder="Street address"
                />
              </div>

              <div className="form-row state-tax-address-row">
                <div className="form-group">
                  <span>City</span>
                  <input
                    type="text"
                    className="form-control"
                    field="form.city"
                    value={form.city || ""}
                    onChange={onFormChange}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <span>State</span>
                  <input
                    type="text"
                    className="form-control"
                    field="form.state"
                    value={form.state || ""}
                    onChange={onFormChange}
                    placeholder="State"
                  />
                </div>
                <div className="form-group">
                  <span>ZIP Code</span>
                  <input
                    type="text"
                    className="form-control"
                    field="form.zip_code"
                    value={form.zip_code || ""}
                    onChange={onFormChange}
                    placeholder="ZIP"
                  />
                </div>
              </div>

              <p className="state-tax-boise-declaration">
                Under penalties of perjury, I declare that to the best of my
                knowledge and belief I can claim the number of withholding
                allowances on line 1 above.
              </p>
            </div>

            <div className="row form-row">
              <div className="col">
                <div className="inputs-col">
                  <div className="inputs-head">
                    Signature
                    <span className="required-star">&#x2A;</span>
                  </div>
                  <div className="inputs-items">
                    <div ref={setSignContainer} className="sign_here">
                      {sign && (
                        <Fragment>
                          <SignPad
                            id="stateTaxBoiseSignPad"
                            height={sign.height || 144}
                            width={sign.width || 446}
                            handleSignature={onSignature}
                            signature={form.employee_signature}
                          />
                          <input
                            type="hidden"
                            value={form.employee_signature || ""}
                            name="employee_signature"
                            field="form.employee_signature"
                            required
                          />
                          {errors["form.employee_signature"] && (
                            <label
                              htmlFor="employee_signature"
                              className="error_label"
                            >
                              {errors["form.employee_signature"]}
                            </label>
                          )}
                        </Fragment>
                      )}
                    </div>
                  </div>
                </div>
                <div className="inputs-col">
                  <div className="inputs-head">
                    Date<span className="required-star">&#x2A;</span>
                  </div>
                  <div className="inputs-items">
                    <DatePicker
                      className={`${errors["form.signed_date"] ? "error" : ""} w-100`}
                      name="signed_date"
                      id="signed_date_boise"
                      required
                      selected={moment(form.signed_date)}
                      field="form.signed_date"
                      placeholder="mm/dd/yyyy"
                      onChange={(date) =>
                        onDateChange("form.signed_date", date)
                      }
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
            </div>
          

          <div className="row form-row last">
            <div className="col">
              <div className="inputs-col">
                <div className="check_radio_group check_disclaimer">
                  <div className="check_radio_item">
                    <input
                      type="checkbox"
                      name="disclaimer_check"
                      id="disclaimer_check_boise_p1"
                      field="form.disclaimer_check"
                      checked={!!form.disclaimer_check}
                      onChange={onFormChange}
                      required
                    />
                    <span className="checkbox_checkbox" />
                    <label htmlFor="disclaimer_check_boise_p1">
                      I have confirmed the above details are true and valid.
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row state-tax-boise-w4">
      <div className="col-md-12">
        <section className="state-tax-boise-sheet state-tax-boise-sheet--p2">
          <h3 className="mt-4 mb-3">Form ID W-4 (continued)</h3>
          <div className="state-tax-boise-green-rule" />

          <div className="state-tax-boise-page2-body">
            <p className="state-tax-boise-page2-strong">
              <strong>Line 1. Total number of allowances you&apos;re claiming.</strong>
            </p>
            <p>
              Enter the number of children in your household age 16 or under as
              of December 31, 2025. If you have no qualifying children, enter
              &quot;0.&quot; If your filing status will be head of household on your
              tax return, add &quot;2&quot; to the number of qualifying children.{" "}
              <strong>Don&apos;t claim allowances for you or your spouse.</strong>{" "}
              You can claim fewer allowances but not more.
            </p>
            <p>
              If you&apos;re married, claim your allowances on this ID W-4 for the
              highest-paying job for the most accurate withholding. If you&apos;re
              married filing jointly, only one of you should claim the allowances.
              The other should claim zero allowances.
            </p>
            <p>
              If you work for more than one employer at the same time, you should
              claim zero allowances on your ID W-4 with any employer other than
              your principal employer.
            </p>
            <p>
              Write <strong>Exempt</strong> on line 1 if you meet{" "}
              <strong>both</strong> of the following conditions:
            </p>
            <ul className="state-tax-boise-list">
              <li>Last year had no Idaho income tax liability.</li>
              <li>This year expect to have no Idaho income tax liability.</li>
            </ul>

            <h4 className="state-tax-boise-nra-title">Nonresident Aliens</h4>
            <p>
              <strong>Exempt income.</strong> If you&apos;re a nonresident alien and
              all your income is exempt from withholding, write{" "}
              <strong>Exempt</strong> on line 1.
            </p>
            <p>
              <strong>Exempt income from a treaty.</strong> If a treaty exempts a
              portion of your income from withholding, complete federal Form 8233
              to claim your treaty benefits, and complete this ID W-4 to withhold
              on income that your treaty doesn&apos;t exempt.
            </p>
            <p>
              <strong>Idaho taxable income.</strong> If you&apos;re a nonresident
              alien and have Idaho taxable income, do all of these:
            </p>
            <ol className="state-tax-boise-ol">
              <li>
                Check the &quot;<strong>A</strong>&quot; box (Single) withholding
                regardless of your marital status.
              </li>
              <li>Enter 0 on line 1.</li>
              <li>
                Using the Pay Period table below, enter the additional amount of
                income tax you want withheld each pay period on line 2.{" "}
                <em>Exception</em>: If you&apos;re a student or business apprentice
                from India, report $0 on line 2.
              </li>
            </ol>

            <table className="state-tax-boise-table">
              <tbody>
                <tr>
                  <th colSpan={5}>Pay Period Table</th>
                </tr>
                <tr>
                  <td>If your pay period is:</td>
                  <td>Weekly</td>
                  <td>Biweekly</td>
                  <td>Semimonthly</td>
                  <td>Monthly</td>
                </tr>
                <tr>
                  <td>Enter this amount on line 2:</td>
                  <td>$15</td>
                  <td>$31</td>
                  <td>$33</td>
                  <td>$66</td>
                </tr>
              </tbody>
            </table>

            <p className="state-tax-boise-table-note">
              The withholding table calculations for employers include the standard
              deduction. Because nonresident aliens don&apos;t qualify for the
              standard deduction, the Pay Period table helps ensure that employers
              withhold enough.
            </p>

            <p className="state-tax-boise-page2-strong">
              <strong>
                Line 2. Additional amount, if any, you need withheld from each
                paycheck.
              </strong>
            </p>
            <p>
              <strong>
                If you&apos;re single or married filing separately and have more than
                one job at a time
              </strong>
              , complete the worksheet below to calculate any additional amount
              you need withheld from each paycheck.
            </p>

            <div className="state-tax-boise-worksheet">
              <div className="state-tax-boise-stack-field">
                <div className="state-tax-boise-stack-field-label">
                  <ul>
                    <li>
                      Other than your primary job, how many jobs do you expect to
                      have at the same time during 2025? (Don&apos;t count your
                      primary job.)
                    </li>
                  </ul>
                </div>
                <div className="state-tax-boise-stack-field-input">
                  <input
                    type="number"
                    placeholder="- Enter here -"
                    className="price_input"
                    name="boise_worksheet_other_jobs"
                    id="boise_ws_other_jobs"
                    value={form.boise_worksheet_other_jobs ?? ""}
                    field="form.boise_worksheet_other_jobs"
                    onChange={onFormChange}
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={preventNegativeValueInDocs}
                  />
                </div>
              </div>

              <div className="state-tax-boise-stack-field">
                <div className="state-tax-boise-stack-field-label">
                  <ul>
                    <li>Multiply the number on line 1 by $15,000</li>
                  </ul>
                </div>
                <div className="state-tax-boise-stack-field-input">
                  <input
                    type="text"
                    readOnly
                    tabIndex={-1}
                    className="price_input"
                    id="boise_ws_line2"
                    value={form.boise_worksheet_itemized_line_2 ?? ""}
                    aria-label="Line 2, multiply line 1 by 15000"
                  />
                </div>
              </div>

              <div className="state-tax-boise-stack-field">
                <div className="state-tax-boise-stack-field-label">
                  <ul>
                    <li>
                      Enter an estimate of your 2025 income from other jobs (not
                      including your primary job)
                    </li>
                  </ul>
                </div>
                <div className="state-tax-boise-stack-field-input">
                  <input
                    type="number"
                    placeholder="- Enter here -"
                    className="price_input"
                    name="boise_worksheet_other_income"
                    id="boise_ws_other_income"
                    value={form.boise_worksheet_other_income ?? ""}
                    field="form.boise_worksheet_other_income"
                    onChange={onFormChange}
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={preventNegativeValueInDocs}
                  />
                </div>
              </div>

              <div className="state-tax-boise-stack-field">
                <div className="state-tax-boise-stack-field-label">
                  <ul>
                    <li>Enter the smaller of lines 2 or 3</li>
                  </ul>
                </div>
                <div className="state-tax-boise-stack-field-input">
                  <input
                    type="text"
                    readOnly
                    tabIndex={-1}
                    className="price_input"
                    id="boise_ws_line4"
                    value={form.boise_worksheet_itemized_line_4 ?? ""}
                    aria-label="Line 4, smaller of lines 2 or 3"
                  />
                </div>
              </div>

              <div className="state-tax-boise-stack-field">
                <div className="state-tax-boise-stack-field-label">
                  <ul>
                    <li>
                      If you completed the{" "}
                      <em>Itemized Deduction Worksheet for Idaho</em> (
                      <a
                        href="https://tax.idaho.gov/w4"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        tax.idaho.gov/w4
                      </a>
                      ), enter the number from line 4. Otherwise, enter
                      &quot;0&quot;
                    </li>
                  </ul>
                </div>
                <div className="state-tax-boise-stack-field-input">
                  <input
                    type="number"
                    placeholder="- Enter here -"
                    className="price_input"
                    name="boise_worksheet_itemized_line4"
                    id="boise_ws_itemized_line4"
                    value={form.boise_worksheet_itemized_line4 ?? ""}
                    field="form.boise_worksheet_itemized_line4"
                    onChange={onFormChange}
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={preventNegativeValueInDocs}
                  />
                </div>
              </div>

              <div className="state-tax-boise-stack-field">
                <div className="state-tax-boise-stack-field-label">
                  <ul>
                    <li>Multiply the number on line 5 by $3,868</li>
                  </ul>
                </div>
                <div className="state-tax-boise-stack-field-input">
                  <input
                    type="text"
                    readOnly
                    tabIndex={-1}
                    className="price_input"
                    id="boise_ws_line6"
                    value={form.boise_worksheet_itemized_line_6 ?? ""}
                    aria-label="Line 6, multiply line 5 by 3868"
                  />
                </div>
              </div>

              <div className="state-tax-boise-stack-field">
                <div className="state-tax-boise-stack-field-label">
                  <ul>
                    <li>Subtract line 6 from line 4</li>
                  </ul>
                </div>
                <div className="state-tax-boise-stack-field-input">
                  <input
                    type="text"
                    readOnly
                    tabIndex={-1}
                    className="price_input"
                    id="boise_ws_line7"
                    value={form.boise_worksheet_itemized_line_7 ?? ""}
                    aria-label="Line 7, line 4 minus line 6"
                  />
                </div>
              </div>

              <div className="state-tax-boise-stack-field">
                <div className="state-tax-boise-stack-field-label">
                  <ul>
                    <li>
                      Multiply line 7 by 5.3% (.053). This is the additional amount
                      you need to withhold annually
                    </li>
                  </ul>
                </div>
                <div className="state-tax-boise-stack-field-input">
                  <input
                    type="text"
                    readOnly
                    tabIndex={-1}
                    className="price_input"
                    id="boise_ws_line8"
                    value={form.boise_worksheet_itemized_line_8 ?? ""}
                    aria-label="Line 8, annual additional withholding"
                  />
                </div>
              </div>

              <div className="state-tax-boise-stack-field">
                <div className="state-tax-boise-stack-field-label">
                  <ul>
                    <li>
                      Divide the amount on line 8 by your remaining pay periods.
                      Enter this amount on line 2 of the W-4 as the additional
                      amount you need withheld from each paycheck
                    </li>
                  </ul>
                </div>
                <div className="state-tax-boise-stack-field-input">
                  <input
                    type="text"
                    readOnly
                    tabIndex={-1}
                    className="price_input"
                    id="boise_ws_per_paycheck"
                    value={form.boise_worksheet_itemized_line_9 ?? ""}
                    aria-label="Additional withholding per paycheck"
                  />
                </div>
              </div>
            </div>

          </div>
        </section>
      </div>
    </div>
  );
}
